import React, { useState } from 'react';
import { Button, DatePicker, Divider, Flex, theme, Tooltip, Avatar, Badge, Layout, Typography } from 'antd';
import {
    RightOutlined,
    LeftOutlined,
    FilterOutlined,
    HomeOutlined,
    DeleteOutlined,
    EyeOutlined,
    EditOutlined,
    UserOutlined,
    AntDesignOutlined,
    SendOutlined,
    VideoCameraOutlined,
    MoreOutlined,
    LikeOutlined,
    DislikeOutlined,
    HistoryOutlined,
    CalendarOutlined
} from '@ant-design/icons';
import { COLOR } from '../../../../theme/constant';

const { Header, Footer, Sider, Content } = Layout;
const { Title, Text } = Typography;


export default function DayView() {
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth()); // Default to the current month
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Default to the current year
    const [selectedDay, setSelectedDay] = useState(new Date().getDate()); // Default to the current day

    const months = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    // Function to get the number of days in a given month and year
    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const days = Array.from({ length: getDaysInMonth(selectedYear, selectedMonth) }, (_, i) => i + 1);

    const meetingsSchedule = {
        "1": ["Google", "Teams"],
        "2": ["Octoplex"],
        "3": ["Google", "Teams", "Octoplex"],
        "4": [],
        "5": ["Teams"],
        "6": ["Google"],
        "7": [],
        "8": ["Octoplex"],
        "9": ["Google", "Teams"],
        "10": [],
        "11": ["Teams"],
        "12": ["Google", "Octoplex"],
        "13": [],
        "14": ["Teams", "Octoplex"],
        "15": ["Google"],
        "16": [],
        "17": ["Teams"],
        "18": ["Google", "Teams", "Octoplex"],
        "19": [],
        "20": ["Octoplex"],
        "21": ["Google"],
        "22": ["Teams", "Google"],
        "23": [],
        "24": ["Octoplex", "Teams"],
        "25": ["Google"],
        "26": [],
        "27": ["Teams"],
        "28": ["Google", "Octoplex"],
        "29": ["Google"],
        "30": ["Teams"],
        "31": ["Google", "Teams", "Octoplex"]
    };

    const {
        token: { colorBgContainer, borderRadiusLG, colorPrimary, colorWarning, colorTextDescription },
    } = theme.useToken();

    const styles = {
        hiddenScroll: {
            display: 'flex',
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            scrollbarWidth: 'none', // Hides scrollbar for Firefox
            msOverflowStyle: 'none', // Hides scrollbar for IE and Edge
        },
        flexContainer: {
            margin: '16px',
            borderRadius: borderRadiusLG,
        },
        buttonMargin: {
            margin: '0 8px',
        },
        highlightBox: {
            backgroundColor: colorPrimary,
            padding: '8px 12px',
            borderRadius: borderRadiusLG,
        },
        line: {
            height: 4,
            marginTop: 4,
            borderRadius: 4,
            width: '100%',
            display: 'flex', // Flex container to allow each segment to align next to each other
        },
        segment: {
            height: '100%',
        },
        colors: {
            Teams: { color: COLOR.amethyst },
            Google: { color: colorPrimary },
            Octoplex: { color: colorWarning },
        },
    };

    // Function to calculate the width distribution of the line
    const getLineStyle = (meetings) => {
        const totalMeetings = meetings.length;
        const width = `${100 / totalMeetings}%`;
        return meetings.map((meeting) => ({
            width,
            backgroundColor: styles.colors[meeting].color,
        }));
    };

    return (
        <>
            {/* Months List with Horizontal Scrolling */}
            <Flex gap={20} justify="space-between" align="center" style={styles.flexContainer}>
                <Flex gap={20} justify="flex-start" align="center">
                    <DatePicker
                        placeholder={selectedYear.toString()}
                        picker="year"
                        style={{ minWidth: 88 }}
                        onChange={(date) => setSelectedYear(date.year())}
                    />
                    <Button
                        icon={<LeftOutlined style={{ paddingLeft: 8, paddingRight: 8 }} />}
                        onClick={() => setSelectedMonth((prev) => (prev > 0 ? prev - 1 : 11))}
                    />
                    <Button
                        icon={<RightOutlined style={{ paddingLeft: 8, paddingRight: 8 }} />}
                        onClick={() => setSelectedMonth((prev) => (prev < 11 ? prev + 1 : 0))}
                    />
                    <Button
                        onClick={() => {
                            const today = new Date();
                            setSelectedMonth(today.getMonth());
                            setSelectedDay(today.getDate());
                            setSelectedYear(today.getFullYear());
                        }}
                    >
                        Today
                    </Button>
                </Flex>
                <div style={{ overflow: 'hidden' }}>
                    <div style={{ ...styles.hiddenScroll, gap: 20 }}>
                        {months.map((month, index) => (
                            <Button
                                size='small'
                                key={index}
                                type={index === selectedMonth ? 'primary' : 'text'}
                                onClick={() => setSelectedMonth(index)}
                            >
                                {month}
                            </Button>
                        ))}
                    </div>
                </div>
            </Flex>
            {/* <Divider /> */}

            {/* Days List with Horizontal Scrolling */}
            <Flex gap={16} justify="space-between" align="center" style={styles.flexContainer}>
                <Button
                    icon={<LeftOutlined />}
                    onClick={() =>
                        setSelectedDay((prev) => (prev > 1 ? prev - 1 : days.length))
                    }
                />
                <div style={{ overflow: 'hidden', width: '100%' }}>
                    <div style={styles.hiddenScroll}>
                        {days.map((day, index) => (
                            <div key={index} style={styles.buttonMargin}>
                                <Button
                                    size='small'
                                    style={{ minWidth: 36 }}
                                    type={day === selectedDay ? 'primary' : 'text'}
                                    onClick={() => setSelectedDay(day)}
                                >
                                    {day}
                                </Button>
                                {/* Render line if there are meetings */}
                                {meetingsSchedule[day] && meetingsSchedule[day].length > 0 && (
                                    <div style={styles.line}>
                                        {getLineStyle(meetingsSchedule[day]).map((lineStyle, idx) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    ...lineStyle,
                                                    ...styles.segment,
                                                }}
                                            ></div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                <Button
                    icon={<RightOutlined />}
                    onClick={() =>
                        setSelectedDay((prev) => (prev < days.length ? prev + 1 : 1))
                    }
                />
            </Flex>
            <Divider />
            {/* Display Selected Date */}
            <Flex gap={20}
                justify="flex-start"
                align="center"
                style={{
                    margin: '16px',
                    fontSize: 24,
                    // padding: '12px 16px',
                    // background: colorBgContainer,
                    // borderRadius: borderRadiusLG,
                    // marginBottom: 8
                }}>
                <div style={styles.highlightBox}>
                    {String(selectedDay).padStart(2, '0')}
                </div>
                <div>Tuesday, {months[selectedMonth]}, {selectedYear}</div>
            </Flex>

            {/* Display Meetings for Selected Day */}
            <div style={{ margin: '16px' }}>
                {meetingsSchedule[selectedDay]?.length > 0 ? (
                    <>
                        {meetingsSchedule[selectedDay].map((meeting, idx) => (
                            <Badge.Ribbon text="RSVP" color={colorPrimary} key={idx}>
                                <div style={{
                                    padding: '12px 16px',
                                    background: colorBgContainer,
                                    borderRadius: borderRadiusLG,
                                    marginBottom: 8,
                                    borderLeft: `4px solid ${styles.colors[meeting]?.color || 'grey'}`,
                                }}>
                                    <Flex align='center' justify='space-between'>
                                        <Flex vertical>
                                            <Content>
                                                <span style={{ fontWeight: 800 }}>{meeting} Conference</span>
                                                <br />
                                                <span style={{ fontSize: 12, color: colorTextDescription }}>
                                                    April {selectedDay}, {selectedYear} | 11:00 AM - 11:30 AM
                                                </span>
                                            </Content>
                                            <Avatar.Group style={{ padding: '8px 0' }}>
                                                <Avatar src="https://api.dicebear.com/7.x/miniavs/svg?seed=1" />
                                                <a href="https://ant.design">
                                                    <Avatar
                                                        style={{
                                                            backgroundColor: '#f56a00',
                                                        }}
                                                    >
                                                        K
                                                    </Avatar>
                                                </a>
                                                <Tooltip title="Ant User" placement="top">
                                                    <Avatar
                                                        style={{
                                                            backgroundColor: '#87d068',
                                                        }}
                                                        icon={<UserOutlined />}
                                                    />
                                                </Tooltip>
                                                <Avatar
                                                    style={{
                                                        backgroundColor: '#1677ff',
                                                    }}
                                                    icon={<AntDesignOutlined />}
                                                />
                                            </Avatar.Group>
                                        </Flex>
                                        <Flex align='center' justify='flex-end' gap={16}>
                                            <Button type="text" icon={<SendOutlined />} />
                                            <Button type="text" icon={<VideoCameraOutlined />} />
                                            <Button type="text" icon={<MoreOutlined />} />
                                        </Flex>
                                    </Flex>
                                </div>
                            </Badge.Ribbon>
                        ))}
                    </>
                ) : (
                    <Flex align='center' justify='center' vertical style={{marginTop:64}}>
                        <div style={{
                            margin: '16px',
                            padding: '16px',
                            background: colorBgContainer,
                            borderRadius: borderRadiusLG,
                        }}>
                            <CalendarOutlined style={{ color: colorPrimary, fontSize: 36 }} />
                        </div>
                        <Text style={{color:colorTextDescription}}>Meeting Not Scheduled</Text>
                    </Flex>
                )}
            </div>
        </>
    );
}
